import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, BookOpen, LineChart, Repeat2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import SEO, { orgSchema, breadcrumbSchema, faqSchema } from "@/components/SEO";
import { PageHero, SectionHeading, FinalCTA } from "@/components/sections";
import { Reveal, stagger, item } from "@/components/motion";
import CBTCycle from "@/components/CBTCycle";
import Icon from "@/components/Icon";
import { CONDITIONS, FAQS } from "@/data/content";
import { useAppointment } from "@/components/AppointmentContext";

const BENEFITS = [
  { icon: "Timer", t: "Faster results", d: "Meaningful change often in 12–20 sessions, not years." },
  { icon: "Wrench", t: "Practical skills", d: "Tools you can apply the same week you learn them." },
  { icon: "LineChart", t: "Measurable progress", d: "Track improvement with validated assessments." },
  { icon: "ShieldCheck", t: "Lower relapse", d: "You learn to be your own therapist for life." },
  { icon: "Target", t: "Goal-focused", d: "Every session has clear purpose and direction." },
  { icon: "HeartHandshake", t: "Collaborative", d: "You and your therapist work as a team." },
];

const EXPECT = [
  { icon: BookOpen, t: "Structured sessions", d: "Each session has an agenda and builds on the last — no aimless talking." },
  { icon: Repeat2, t: "Skills practice", d: "You'll learn and rehearse concrete techniques in session." },
  { icon: LineChart, t: "Homework that works", d: "Small, doable between-session exercises turn insight into habit." },
  { icon: ShieldCheck, t: "Progress reviews", d: "Regular check-ins with real data keep therapy on track." },
];

const CBT_FAQS = [
  { q: "Is CBT scientifically proven?", a: "Yes. CBT is the most extensively researched form of psychotherapy in the world, with hundreds of clinical trials supporting its effectiveness. It's recommended as a first-line treatment for anxiety, depression, OCD, PTSD, and more by major health organisations." },
  { q: "How is CBT different from regular talk therapy?", a: "Traditional talk therapy is often open-ended and focused on exploring the past. CBT is structured, present-focused, and goal-oriented — it gives you practical skills and measurable progress, usually in a shorter timeframe." },
  { q: "Do I really have to do homework?", a: "Between-session practice is one of the biggest predictors of success in CBT. But don't worry — your therapist tailors homework to be doable and relevant. It's where lasting change actually happens." },
  { q: "How long does CBT take?", a: "It varies by individual and goal, but many people experience meaningful improvement within 12–20 sessions. Your plan is always personalised, and progress is tracked along the way." },
  { q: "Can CBT be done via Telehealth?", a: "Absolutely. Research shows Telehealth CBT is just as effective as in-person for most concerns. We offer secure, HIPAA-compliant video sessions across Connecticut." },
];

export default function CBT() {
  const { openDialog } = useAppointment();
  const crumbs = [{ name: "Home", path: "/" }, { name: "Cognitive Behavioral Therapy", path: "/cognitive-behavioral-therapy" }];
  return (
    <>
      <SEO title="Cognitive Behavioral Therapy (CBT)" path="/cognitive-behavioral-therapy"
        description="Comprehensive guide to Cognitive Behavioral Therapy at Beacon Counseling, Connecticut. What CBT is, how it works, the science, what to expect, and the conditions it treats."
        jsonLd={[orgSchema, breadcrumbSchema(crumbs), faqSchema(CBT_FAQS)]} />
      <PageHero eyebrow="Cognitive Behavioral Therapy" breadcrumb={crumbs}
        title="The gold standard of modern psychotherapy."
        intro="CBT is practical, structured, and proven — teaching you to change unhelpful thoughts and behaviours so you feel measurably better, faster."
        image="https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />

      {/* What is CBT */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-3xl px-6 lg:px-12">
          <SectionHeading eyebrow="What is CBT?" title="Change your thinking. Change your life." center />
          <Reveal delay={0.1}>
            <div className="mt-8 space-y-5 text-lg text-muted-foreground leading-relaxed">
              <p>Cognitive Behavioral Therapy (CBT) is a structured, evidence-based form of psychotherapy built on a powerful insight: it's not events themselves that determine how we feel, but the way we <em className="text-navy not-italic font-medium">think about</em> and <em className="text-navy not-italic font-medium">respond to</em> them.</p>
              <p>By learning to identify and reshape unhelpful thought patterns and behaviours, you can transform how you feel — and build skills that last a lifetime. CBT is present-focused and practical: less time spent analysing the past, more time building the tools to change your present.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How CBT works - cycle */}
      <section className="bg-secondary py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="How CBT Works" title="See the cycle. Interrupt the cycle."
            intro="Thoughts, feelings, and behaviours are deeply connected. CBT teaches you where to intervene." />
          <div className="mt-16"><CBTCycle /></div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Benefits" title="Why people choose CBT." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b) => (
              <motion.div key={b.t} variants={item} className="rounded-3xl border border-navy-100 bg-white p-7 shadow-soft transition-transform hover:-translate-y-1">
                <span className="grid h-13 w-13 place-items-center rounded-2xl bg-softblue/60 p-3 text-teal"><Icon name={b.icon} className="h-6 w-6" /></span>
                <h3 className="mt-5 font-display text-xl text-navy">{b.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Scientific evidence band */}
      <section className="bg-navy py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-teal-light">The Science</span>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight text-white text-balance">Backed by decades of research.</h2>
              <p className="mt-5 text-lg text-white/65 leading-relaxed">CBT is the most studied form of psychotherapy in existence — recommended as a first-line treatment by leading health bodies worldwide for a wide range of conditions.</p>
            </div>
            <div className="grid grid-cols-2 gap-5">
              {[{ n: "2,000+", l: "Published clinical studies" }, { n: "#1", l: "Recommended for anxiety & depression" }, { n: "12–20", l: "Typical sessions to see change" }, { n: "Lower", l: "Relapse rates vs. medication alone" }].map((s) => (
                <Reveal key={s.l}>
                  <div className="rounded-3xl glass-navy p-7">
                    <div className="font-display text-4xl text-teal-light">{s.n}</div>
                    <div className="mt-2 text-sm text-white/60">{s.l}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to expect + homework + progress */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="What to Expect" title="Structured, practical, and transparent." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {EXPECT.map((e) => (
              <motion.div key={e.t} variants={item} className="rounded-3xl bg-secondary p-7">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white text-teal shadow-soft"><e.icon className="h-6 w-6" /></span>
                <h3 className="mt-5 font-display text-lg text-navy">{e.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{e.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Conditions treated */}
      <section className="bg-secondary py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Conditions Treated" title="What CBT can help with." />
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            {CONDITIONS.map((c) => (
              <Link key={c.name} to={c.to} className="group flex items-center gap-2 rounded-full border border-navy-100 bg-white px-5 py-2.5 shadow-soft transition-all hover:-translate-y-0.5 hover:border-teal/40">
                <Icon name={c.icon} className="h-4 w-4 text-teal" />
                <span className="text-navy/80">{c.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose CBT at Beacon */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Therapist and client in a warm session" className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-premium" />
            </Reveal>
            <div>
              <SectionHeading eyebrow="Why Beacon" title="CBT, delivered with genuine warmth." />
              <div className="mt-6 space-y-3">
                {["Fully licensed, CBT-trained clinicians", "Personalised, goal-oriented treatment plans", "Measurement-based care that tracks your progress", "Telehealth & in-person options across Connecticut", "A calm, non-judgmental, collaborative space"].map((t, i) => (
                  <Reveal key={t} delay={i * 0.05}>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-teal/10 text-teal"><Check className="h-3.5 w-3.5" /></span>
                      <span className="text-navy/85">{t}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.25}>
                <Button onClick={openDialog} className="mt-8 rounded-full bg-teal hover:bg-teal-dark text-white h-13 px-7 hover:-translate-y-0.5 transition-transform">
                  Start CBT with Beacon <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-secondary py-24 lg:py-28">
        <div className="mx-auto max-w-3xl px-6 lg:px-12">
          <SectionHeading center eyebrow="FAQ" title="Your CBT questions, answered." />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-12">
              {CBT_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`c-${i}`} className="border-navy-100">
                  <AccordionTrigger className="text-left font-display text-lg text-navy hover:text-teal hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <FinalCTA title="Experience therapy that works." text="Ready to trade endless talking for practical change? Let's build your CBT plan together." />
    </>
  );
}
